// src/lib/media/resolveMediaAssetToUrl.ts
//
// Phase M1: resolve MediaAssetV1.ref -> playable URL.
// Supports: url | static | firebase | registry
//
// Registry shape (suggested):
//  collection: mediaRegistry
//  doc id: <key>
//  fields: { v: 1, provider: "firebase"|"url", storagePath?: string, url?: string, kind?: "audio"|"video"|"image" }

import { doc, getDoc } from "firebase/firestore";
import { getDownloadURL, ref as storageRef } from "firebase/storage";

import type { MediaAssetV1, MediaRefV1 } from "@/contracts/media/MediaAssetV1";
import { getDbOrThrow, getStorageOrThrow } from "@/lib/firebase";

type RegistryDocV1 = {
  v: 1;
  provider: "firebase" | "url";
  storagePath?: string;
  url?: string;
  kind?: "audio" | "video" | "image";
};

export async function resolveMediaRefToUrl(refIn: MediaRefV1): Promise<string | null> {
  if (!refIn || refIn.v !== 1) return null;

  if (refIn.provider === "url") {
    return String(refIn.url);
  }

  if (refIn.provider === "static") {
    // Vite public assets served from /
    return String(refIn.path);
  }

  if (refIn.provider === "firebase") {
    // Firebase Storage object path -> downloadURL
    const storage = getStorageOrThrow();
    const r = storageRef(storage, refIn.storagePath);
    return await getDownloadURL(r);
  }

  if (refIn.provider === "registry") {
    // Firestore -> resolve to url or firebase
    const db = getDbOrThrow();
    const d = await getDoc(doc(db, "mediaRegistry", refIn.key));
    if (!d.exists()) return null;
    const data = d.data() as Partial<RegistryDocV1>;

    if (data.provider === "url" && data.url) return String(data.url);
    if (data.provider === "firebase" && data.storagePath) {
      const storage = getStorageOrThrow();
      const r = storageRef(storage, String(data.storagePath));
      return await getDownloadURL(r);
    }
    return null;
  }

  return null;
}

export async function resolveAssetToUrl(asset: MediaAssetV1): Promise<string | null> {
  if (!asset) return null;
  return resolveMediaRefToUrl(asset.ref);
}
